import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/Button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { 
  Award, 
  Users, 
  Heart, 
  Shield, 
  Star,
  MapPin,
  Calendar,
  CheckCircle,
  Phone
} from 'lucide-react';
import { ImageWithFallback } from './figma/ImageWithFallback';

const stats = [
  { label: 'Licensed Providers', value: '50+', icon: Users },
  { label: 'Patients Served', value: '10,000+', icon: Heart },
  { label: 'Partner Facilities', value: '500+', icon: Award },
  { label: 'Years of Excellence', value: '15+', icon: Star }
];

const values = [
  {
    title: 'Compassionate Care',
    description: 'We believe every individual deserves empathetic, personalized mental health treatment that honors their unique journey.',
    icon: Heart
  },
  {
    title: 'Clinical Excellence',
    description: 'Our providers maintain the highest clinical standards through continuous education and evidence-based practice.',
    icon: Award
  },
  {
    title: 'Accessibility',
    description: 'Mental health care should be accessible to everyone, regardless of location or circumstances.',
    icon: Users
  },
  {
    title: 'Privacy & Security',
    description: 'We maintain the strictest confidentiality and security standards to protect patient information.',
    icon: Shield
  }
];

const leadership = [
  {
    name: 'Dr. Jennifer Martinez',
    title: 'Chief Medical Officer',
    specialty: 'General Psychiatry',
    experience: '20 years',
    image: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
    bio: 'Board-certified psychiatrist with extensive experience in telehealth and facility-based care.'
  },
  {
    name: 'Michael Thompson',
    title: 'Chief Executive Officer',
    specialty: 'Healthcare Administration',
    experience: '15 years',
    image: 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
    bio: 'Healthcare executive focused on expanding access to quality mental health services.'
  },
  {
    name: 'Dr. Sarah Kim',
    title: 'Director of Clinical Operations',
    specialty: 'Child & Adolescent Psychiatry',
    experience: '12 years',
    image: 'https://images.unsplash.com/photo-1594824928070-3546ab29b64c?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
    bio: 'Pediatric psychiatry specialist committed to improving youth mental health outcomes.'
  }
];

const accreditations = [
  'Joint Commission Accredited',
  'HIPAA Compliant',
  'CMS Certified',
  'State Licensed in All 50 States',
  'NCQA Healthcare Equality Index',
  'Better Business Bureau A+ Rating'
];

export const AboutUs = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-8">
            <div className="space-y-4">
              <h1 className="text-5xl text-gray-900 leading-tight">
                Transforming Mental Health Care 
                <span className="text-blue-600"> Nationwide</span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed max-w-4xl mx-auto">
                Since 2009, MindCare has been pioneering accessible, high-quality psychiatric services through 
                innovative telehealth technology and compassionate onsite care for facilities across America.
              </p>
            </div>
            
            <div className="flex justify-center">
              <Button size="lg" asChild>
                <Link to="/contact" className='no-underline'>
                  Get in Touch
                  <Phone className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center space-y-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                  <stat.icon className="h-8 w-8 text-blue-600" />
                </div>
                <div className="space-y-2">
                  <div className="text-4xl text-gray-900">{stat.value}</div>
                  <div className="text-gray-600">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <h2 className="text-4xl text-gray-900">Our Mission</h2>
                <p className="text-xl text-gray-600 leading-relaxed">
                  To provide exceptional mental health care that is accessible, compassionate, and clinically excellent. 
                  We bridge the gap between patients and quality psychiatric services through innovative technology 
                  and dedicated professional relationships.
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                {values.map((value, index) => (
                  <div key={index} className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <value.icon className="h-5 w-5 text-blue-600" />
                      </div>
                      <h3 className="text-lg text-gray-900">{value.title}</h3>
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed">{value.description}</p>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <ImageWithFallback
                src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?ixlib=rb-4.0.3&auto=format&fit=crop&w=2340&q=80"
                alt="Healthcare team collaboration"
                className="rounded-2xl shadow-xl w-full h-[600px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl text-gray-900">Leadership Team</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Meet the experienced professionals leading our mission to transform mental health care.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {leadership.map((leader, index) => (
              <Card key={index} className="border border-gray-200 hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6 text-center space-y-4">
                  <ImageWithFallback
                    src={leader.image}
                    alt={leader.name}
                    className="w-24 h-24 rounded-full object-cover mx-auto"
                  />
                  <div className="space-y-2">
                    <h3 className="text-xl text-gray-900">{leader.name}</h3>
                    <p className="text-blue-600">{leader.title}</p>
                    <div className="flex items-center justify-center space-x-4 text-sm text-gray-600">
                      <span>{leader.specialty}</span>
                      <span>•</span>
                      <span>{leader.experience}</span>
                    </div>
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed">{leader.bio}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Accreditations */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl text-gray-900">Credentials & Accreditations</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We maintain the highest standards of quality, safety, and compliance in all our services.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {accreditations.map((accreditation, index) => (
              <div key={index} className="flex items-center space-x-3 bg-white rounded-lg p-4 border border-gray-200">
                <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0" />
                <span className="text-gray-900">{accreditation}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* History Timeline */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl text-gray-900">Our Journey</h2>
            <p className="text-xl text-gray-600">
              A timeline of growth, innovation, and dedication to mental health care excellence.
            </p>
          </div>

          <div className="space-y-8">
            {[
              { year: '2009', title: 'Company Founded', description: 'MindCare was established with a vision to make quality mental health care accessible to underserved communities.' },
              { year: '2012', title: 'First Telehealth Platform', description: 'Launched our first HIPAA-compliant telehealth platform, pioneering remote psychiatric consultations.' },
              { year: '2015', title: 'Facility Partnerships', description: 'Expanded services to include onsite consultations for nursing homes and assisted living facilities.' },
              { year: '2018', title: 'National Expansion', description: 'Achieved licensing in all 50 states, making our services available nationwide.' },
              { year: '2021', title: 'AI Integration', description: 'Implemented AI-driven matching and scheduling systems to improve patient-provider connections.' },
              { year: '2024', title: 'Advanced Platform', description: 'Launched our most advanced platform with enhanced security, analytics, and user experience.' }
            ].map((milestone, index) => (
              <div key={index} className="flex items-start space-x-6">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm">
                    {milestone.year}
                  </div>
                </div>
                <div className="space-y-2 pb-8">
                  <h3 className="text-xl text-gray-900">{milestone.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="custom-section-gradient">
  <div className="custom-container-centered">
          <div className="space-y-8">
            <h2 className="text-4xl text-white">Join Our Mission</h2>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto">
              Whether you're seeking care or looking to partner with us, we're here to help transform mental health outcomes together.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="secondary" asChild>
                <Link to="/providers" className='no-underline'>
                  <Users className="mr-2 h-5 w-5" />
                  Find a Provider
                </Link>
              </Button>
              <Button size="lg" variant="outline"  asChild>
                <Link to="/contact" className='no-underline'>
                  <Phone className="mr-2 h-5 w-5" />
                  Contact Us
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};